import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { Address } from '@ridgewill/brand/react';
import { button, Problem } from './form.js';
import {
  confirmRecoveryReview,
  loadRecoveryAddress,
  type RecoveryState,
} from '../lib/recovery-address.js';
import { deferRecoveryReview, recoveryReviewDeferred } from '../lib/bounce.js';

/**
 * "Is this still your recovery address?" — asked on every screen, once a year.
 *
 * ── WHY IT IS PORTAL-WIDE AND NOT A CARD ON SECURITY ─────────────────
 *
 * Jon's design (2026-09-05): a notification that pops up wherever somebody is
 * in the portal, with Yes and No.
 *
 * The reason it cannot live only on the Security screen is the failure the
 * whole feature is built around. A recovery address decays — set at sign-up,
 * forgotten, and four years later a stranger's inbox — and the people it
 * decays for are precisely the ones who never open Security. A prompt that
 * waits there is a reminder conditioned on the user doing the thing that would
 * have made the reminder unnecessary.
 *
 * ── IT SHOWS THE ADDRESS, WHICH THE EMAIL MAY NEVER DO ───────────────
 *
 * That asymmetry is the point of the whole two-channel design. A recovery
 * address is a second copy of the thing being protected and must not be put in
 * a mailbox, so the reminder email can only say "go and look". Here it is
 * behind a session, on screen, and the question is answerable — which is what
 * makes the answer worth recording.
 *
 * ── NO DISMISS ───────────────────────────────────────────────────────
 *
 * Yes and No both resolve it, and there is no third button. A dismiss would be
 * the one action that changes nothing and stops the asking, which is how a
 * yearly prompt becomes a thing people click past without reading. Somebody who
 * wants it to stop has two ways: confirm the address, or remove it.
 */
export function RecoveryReviewPrompt() {
  const [state, setState] = useState<RecoveryState | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    /**
     * Do not even ASK if this tab has already said No and been sent to the
     * Security card. The navigation is a full page load, so the component that
     * made the decision is gone and this one would otherwise render on top of
     * the card it just sent somebody to.
     */
    if (recoveryReviewDeferred()) return;

    let live = true;
    void loadRecoveryAddress().then((s) => {
      if (live) setState(s);
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * Renders NOTHING in every uncertain case, and that is the opposite of the
   * card on the Security screen.
   *
   * There, a failed lookup must not read as "you have no recovery address",
   * because somebody is looking at that screen to find out. Here the component
   * is unsolicited: a banner drawn from a failed request would interrupt
   * somebody's actual task to ask a question we cannot show them the subject
   * of. Silence is the right failure for an interruption and the wrong one for
   * an answer.
   */
  if (done) return null;
  if (!state || state.status !== 'loaded') return null;
  if (!state.recoveryEmail || !state.reviewDue) return null;

  async function yes() {
    setBusy(true);
    setError(null);
    const out = await confirmRecoveryReview();
    setBusy(false);
    if (typeof out === 'string') {
      setError(out);
      return;
    }
    setDone(true);
  }

  /**
   * "No" navigates rather than doing anything, AND STOPS ASKING.
   *
   * It cannot remove the address itself: that is destructive, keeps a password
   * gate, and would be a one-click deletion of somebody's way back into their
   * own account from a banner they did not go looking for. So it takes them to
   * the place where the address can be replaced or removed, with the same
   * checks as any other time.
   *
   * ── AND THEN IT GOES AWAY (Jon, 2026-09-05) ──────────────────────
   *
   * The first version only navigated, and the banner rendered again on arrival
   * — on top of the very card it had just sent somebody to, still asking a
   * question they had already answered. `deferRecoveryReview` is what ends
   * that, and it is scoped to the tab rather than to the account: this records
   * an answer being ACTED on, not a dismissal, so the next visit asks again if
   * the address is still unconfirmed. It must be set BEFORE the navigation,
   * because nothing after it runs.
   *
   * A full page load rather than a route change, because that is how this app
   * navigates — see the routing note in App.tsx. The `#recovery` fragment is
   * the `Card` id on the Security screen, and `Security` focuses it on arrival:
   * the browser's own fragment jump fires before this app has rendered anything,
   * so the anchor alone silently does nothing.
   */
  function no() {
    deferRecoveryReview();
    window.location.assign('/security#recovery');
  }

  return (
    <aside
      style={{
        border: `1px solid ${C.border}`,
        borderLeft: `4px solid ${C.brand}`,
        borderRadius: '6px',
        background: C.surface,
        padding: '1rem 1.25rem',
        marginBottom: '1.5rem',
      }}
    >
      <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>
        Is this still your recovery address?
      </p>
      <p style={{ margin: '0 0 0.75rem' }}>
        <Address>{state.recoveryEmail}</Address>
      </p>
      <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: C.muted }}>
        This is the mailbox that can get you back into your account if you lose access to your
        main one. We ask once a year, because an address that closed or now belongs to somebody
        else is a way in that nobody is watching.
      </p>

      {error ? <Problem>{error}</Problem> : null}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button type="button" style={button} onClick={() => void yes()} disabled={busy}>
          {busy ? 'Confirming…' : 'Yes, that is still mine'}
        </button>
        <button type="button" style={button} onClick={no} disabled={busy}>
          No, let me change it
        </button>
      </div>
    </aside>
  );
}
